
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, Euro } from "lucide-react";
import { Client } from "@/models/Client";
import { useTranslation } from "react-i18next";
import { useWorkoutTypes } from "@/hooks/useWorkoutTypes";

interface PaymentSummaryCardProps {
  client: Client;
}

const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({ client }) => {
  const { t } = useTranslation();
  const { workoutTypes = [] } = useWorkoutTypes();

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || t('clients.unknownWorkoutType');
  };

  const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
  const totalMonthlyAmount = activeSubscriptions.reduce((sum, sub) => {
    return sum + sub.price; // Changed from calculating with pricePerSession to using price directly
  }, 0);

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-800">
          <Calculator className="h-4 w-4" />
          {t('clients.monthlyAmount')} - {t('clients.activeSubscriptions')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {activeSubscriptions.map((subscription) => {
          return (
            <div key={subscription.id} className="flex items-center justify-between text-sm">
              <span className="text-blue-700">
                {getWorkoutTypeName(subscription.workoutTypeId)} ({subscription.sessionsPerMonth} συνεδρίες)
              </span>
              <span className="font-medium text-blue-800">€{subscription.price.toFixed(2)}</span>
            </div>
          );
        })}
        
        <div className="border-t border-blue-300 pt-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-blue-600" />
            <span className="font-bold text-blue-800">{t('common.total')}:</span>
          </div>
          <span className="text-xl font-bold text-blue-600">
            €{totalMonthlyAmount.toFixed(2)}
          </span>
        </div>
        
        <p className="text-xs text-blue-600 mt-1">
          {activeSubscriptions.length} {activeSubscriptions.length === 1 ? 'συνδρομή' : 'συνδρομές'}
        </p>
      </CardContent>
    </Card>
  );
};

export default PaymentSummaryCard;
